// Proyecto Empresarial — ficha del documento.
//
// SIGUE EL MISMO PATRÓN DE ENTREGA QUE LOS DEMÁS MÓDULOS: la misma barra
// pegada arriba (`view-header`), el mismo cuerpo centrado (`view-content`),
// PDF y Word en los dos idiomas, y el interruptor de idioma con la banda de
// "traducciones pendientes" del proyecto de la visa. Un módulo que se ve
// distinto obliga al operador a aprender dos veces la misma pantalla.
//
// INGLÉS PRIMERO, ESPAÑOL DESPUÉS
// El documento se redacta en inglés (es el idioma del entregable) y el
// backend lo traduce solo con el modelo barato al terminar. Acá se ve el
// avance de esa segunda fase y se puede pedir de nuevo. El interruptor de
// idioma cambia LO QUE SE LEE; la edición siempre es sobre el inglés, que es
// el original, y al guardar se invalida el español de esa sección.
//
// EL AVISO DE LA HOJA FINANCIERA
// Si `financial_sheet` viene en "unavailable", el plan se escribió SIN las
// cifras del cliente: la hoja compartida no se pudo derivar y el modelo, en vez
// de inventar un capital, declaró supuestos. Eso tiene que verse arriba y no
// descubrirse leyendo el plan financiero, porque la acción que corresponde es
// cargar el proyecto en el dossier y regenerar.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Building2, ChevronDown, ChevronUp, Download, Loader2,
  RefreshCw, Save, AlertTriangle, Play, Pencil, Languages, Scissors, FileText,
} from 'lucide-react';
import { API } from '../utils/constants';
import { WordDownloadButton } from '../components/WordDownloadButton';
import { PublishForClientButton } from '../components/PublishForClientButton';

// Los títulos en español para la vista previa. El documento se genera en
// inglés y guarda esos títulos; el backend hace lo mismo al armar el PDF en
// español, porque un documento en español con encabezados "Market Study" está
// a medio traducir.
const TITULOS_ES = [
  'Resumen Ejecutivo', 'Estudio de Mercado', 'Plan Operativo', 'Plan Técnico',
  'Plan Financiero', 'Plan de Implementación', 'Fuentes y Supuestos',
];

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ViewEmpresarial() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [idioma, setIdioma] = useState('en');   // qué versión se está leyendo
  const [abierta, setAbierta] = useState(null);
  const [editando, setEditando] = useState(null);
  const [borrador, setBorrador] = useState('');
  const [ocupado, setOcupado] = useState(null);
  const [verPreview, setVerPreview] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/empresarial/${id}`, { headers: auth() });
      setDoc(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude cargar el proyecto');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras genera o traduce, se refresca solo. Se corta al terminar para no
  // dejar un sondeo eterno abierto en una pestaña olvidada — y también cuando
  // el trabajo MURIÓ: los jobs de fondo viven dentro del proceso de uvicorn,
  // así que un reinicio los mata y el campo queda en 'running' para siempre.
  // Sin esto, la pestaña sondeaba cada 8 segundos indefinidamente.
  const trabajando =
    (doc?.status === 'generating' && !doc?.generation_stalled) ||
    (doc?.translation_status === 'running' && !doc?.translation_stalled);
  useEffect(() => {
    if (!trabajando) return undefined;
    const t = setInterval(cargar, 8000);
    return () => clearInterval(t);
  }, [trabajando, cargar]);

  const regenerar = async (n) => {
    setOcupado(`gen-${n}`);
    try {
      await axios.post(`${API}/empresarial/${id}/generate-section/${n}`, {}, { headers: auth() });
      toast.success(`Sección ${n} regenerada. Se está traduciendo de nuevo.`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude regenerar la sección');
    } finally {
      setOcupado(null);
    }
  };

  const guardar = async (n) => {
    setOcupado(`save-${n}`);
    try {
      await axios.put(`${API}/empresarial/${id}/sections/${n}`,
        { content: borrador }, { headers: auth() });
      toast.success('Sección guardada. Su versión en español quedó pendiente.');
      setEditando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude guardar');
    } finally {
      setOcupado(null);
    }
  };

  const seguirGenerando = async () => {
    setOcupado('resume');
    try {
      const r = await axios.post(`${API}/empresarial/${id}/generate`, {}, { headers: auth() });
      const desde = r.data?.resuming_from || [];
      toast.success(desde.length
        ? `Continuando: ya estaban ${desde.length} secciones`
        : 'Generación iniciada');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude continuar');
    } finally {
      setOcupado(null);
    }
  };

  const traducir = async () => {
    setOcupado('traducir');
    try {
      const r = await axios.post(`${API}/empresarial/${id}/translate`, {}, { headers: auth() });
      toast.success(r.data?.message || 'Traduciendo en segundo plano');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude iniciar la traducción');
    } finally {
      setOcupado(null);
    }
  };

  const descargarPdf = async (lang) => {
    setOcupado(`pdf-${lang}`);
    try {
      const r = await axios.get(`${API}/empresarial/${id}/download-pdf?language=${lang}`,
        { headers: auth(), responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', m ? m[1] : `Proyecto_Empresarial_${lang.toUpperCase()}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF descargado en ${lang === 'es' ? 'español' : 'inglés'}`);
    } catch (e) {
      // El cuerpo del error viene como Blob (responseType:'blob'): sin
      // desenvolverlo, el motivo real —"la traducción no está completa"— se
      // pierde y el operador ve un error genérico.
      let detalle = '';
      const data = e?.response?.data;
      if (data && typeof data.text === 'function') {
        try {
          const texto = await data.text();
          try { detalle = JSON.parse(texto)?.detail || texto; } catch { detalle = texto; }
        } catch { detalle = ''; }
      } else if (typeof data === 'string') {
        detalle = data;
      } else if (data?.detail) {
        detalle = data.detail;
      }
      toast.error(detalle ? String(detalle).slice(0, 300) : 'No pude generar el PDF');
    } finally {
      setOcupado(null);
    }
  };

  if (cargando) {
    return (
      <div style={{ padding: '4rem', textAlign: 'center', color: '#6B7280' }}>
        <Loader2 size={22} className="animate-spin" style={{ margin: '0 auto' }} />
      </div>
    );
  }
  if (!doc) {
    return <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>No encontré el proyecto.</div>;
  }

  const secciones = [...(doc.sections || [])].sort((a, b) => a.number - b.number);
  const escritas = secciones.filter((s) => (s.content || '').trim()).length;
  const traducidas = secciones.filter((s) => (s.content_es || '').trim()).length;
  const generando = doc.status === 'generating';
  // Atascada NO es traduciendo: si no se distinguen, el botón queda
  // deshabilitado para siempre y el operador no puede reintentar. Fue
  // exactamente lo que pasó con un proyecto en 0 de 7 secciones.
  const traduccionAtascada = !!doc.translation_stalled;
  const traduciendo = doc.translation_status === 'running' && !traduccionAtascada;
  const espanolCompleto = escritas > 0 && traducidas >= escritas;
  const pctTraducido = escritas ? Math.round((traducidas / escritas) * 100) : 0;
  const leyendoEs = idioma === 'es';
  const cortadas = secciones.filter((s) => s.truncated).map((s) => s.number);
  // Secciones donde el modelo escribió una serie de cifras como oración en vez
  // de ponerla en una tabla. El prompt ya lo prohíbe y le entrega la tabla
  // hecha, pero una regla de prompt es un pedido, no una garantía.
  const enProsa = secciones.filter((s) => s.prose_series).map((s) => s.number);

  const contenidoDe = (s) => (leyendoEs
    ? (s.content_es || '')
    : (s.content_en || s.content || ''));

  const faltaEspanol = leyendoEs
    ? 'Esta sección todavía no tiene versión en español.'
    : 'Esta sección todavía no se generó.';

  return (
    <div className="view-container">
      <div className="view-header">
        <Button
          variant="ghost"
          onClick={() => navigate(doc.client_id ? `/client-dashboard/${doc.client_id}` : '/dashboard')}
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={18} />
          Volver
        </Button>
        <div className="view-actions" style={{ flexWrap: 'wrap' }}>
          {escritas < 7 && !generando && (
            <Button onClick={seguirGenerando} disabled={ocupado === 'resume'}>
              {ocupado === 'resume'
                ? <Loader2 className="mr-2 animate-spin" size={18} />
                : <Play className="mr-2" size={18} />}
              Continuar
            </Button>
          )}
          {/* El español solo se ofrece cuando existe: un botón que descarga un
              archivo en español con el texto en inglés adentro es peor que un
              botón deshabilitado que dice por qué. */}
          <Button
            onClick={() => descargarPdf('es')}
            variant="outline"
            disabled={!espanolCompleto || ocupado === 'pdf-es'}
            title={espanolCompleto
              ? 'Descargar el PDF en español'
              : `Falta la traducción al español (${traducidas} de ${escritas} secciones)`}
            data-testid="empresarial-pdf-es"
          >
            {ocupado === 'pdf-es'
              ? <Loader2 className="mr-2 animate-spin" size={18} />
              : <Download className="mr-2" size={18} />}
            📄 Descargar PDF (ES)
          </Button>
          <Button
            onClick={() => descargarPdf('en')}
            variant="outline"
            disabled={!escritas || ocupado === 'pdf-en'}
            data-testid="empresarial-pdf-en"
          >
            {ocupado === 'pdf-en'
              ? <Loader2 className="mr-2 animate-spin" size={18} />
              : <Download className="mr-2" size={18} />}
            📄 Descargar PDF (EN)
          </Button>
          <WordDownloadButton
            url={`${API}/empresarial/${id}/download-docx?language=en`}
            testId="empresarial-docx-en"
          />
          <WordDownloadButton
            url={`${API}/empresarial/${id}/download-docx?language=es`}
            label="Descargar Word ES"
            testId="empresarial-docx-es"
            className={espanolCompleto ? '' : 'opacity-50'}
          />
          {/* El mismo portal que el proyecto de la visa: link con token,
              comentarios por sección y rondas de cambios. La ficha de
              portal_registry le quita la marca EB-2, porque este documento no
              habla de la petición. */}
          {escritas > 0 && (
            <PublishForClientButton
              endpoint={`/empresarial/${id}/publish`}
              prefill={{ client_name: doc.founder_name || '' }}
              label="🔗 Compartir con cliente"
              className="bg-amber-50 border-amber-300 hover:bg-amber-100"
            />
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="document-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
            <div style={{ width: 34, height: 34, background: '#ECFDF5', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 size={18} style={{ color: '#0F766E' }} />
            </div>
            <span style={{ color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Proyecto Empresarial
            </span>
          </div>
          <h1 className="document-title">{doc.project_title || 'Proyecto Empresarial'}</h1>
          <p className="document-meta">
            {doc.business_name ? `${doc.business_name} • ` : ''}
            {escritas}/7 secciones • {traducidas}/7 en español
            {doc.created_at ? ` • ${new Date(doc.created_at).toLocaleDateString('es')}` : ''}
          </p>
        </div>

        {generando && (
          <Banda tono="info">
            <Loader2 size={16} className="animate-spin" />
            <div>
              <strong>{doc.progress_message || 'Generando…'}</strong> — {doc.progress || 0}%
              <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
                Podés cerrar esta pantalla: sigue en el servidor.
              </div>
            </div>
          </Banda>
        )}

        {doc.status === 'error' && (
          <Banda tono="error">
            <AlertTriangle size={16} />
            <div>
              <strong>La generación falló.</strong> {doc.error_message}
              <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
                Lo que ya se escribió está guardado: «Continuar» genera solo lo que falta.
              </div>
            </div>
          </Banda>
        )}

        {/* 🌐 La segunda fase: el español. Misma banda que el proyecto de la
            visa, para que se lea igual en los dos módulos. */}
        {escritas > 0 && !espanolCompleto && (
          <Banda tono="traduccion">
            <Languages size={16} />
            <div style={{ flex: 1 }}>
              <strong>Traducción al español pendiente</strong>
              <div style={{ fontSize: '0.85rem', marginTop: 3 }}>
                {traducidas} de {escritas} secciones traducidas ({pctTraducido}%).
                {traduciendo && ' Traduciendo en segundo plano…'}
                {traduccionAtascada && (
                  <strong>{' '}Se atascó: lleva más de {doc.minutes_to_consider_dead || 10} min
                    sin avanzar. Suele pasar cuando se reinicia el servidor a mitad
                    de la traducción. Apretá «Regenerar traducciones» y sigue desde
                    donde quedó.</strong>
                )}
                {!traduciendo && !traduccionAtascada
                  && ' El documento en inglés ya se puede entregar; el español es para que lo revise el cliente.'}
              </div>
              {doc.translation_message && (
                <div style={{ fontSize: '0.8rem', marginTop: 3, opacity: 0.85 }}>
                  {doc.translation_message}
                </div>
              )}
              <div style={{ height: 6, background: '#DBEAFE', borderRadius: 999, overflow: 'hidden', marginTop: 8 }}>
                <div style={{ height: '100%', width: `${pctTraducido}%`, background: '#2563EB', transition: 'width .4s ease' }} />
              </div>
            </div>
            <Button
              onClick={traducir}
              disabled={ocupado === 'traducir' || traduciendo}
              size="sm"
              data-testid="empresarial-translate-btn"
            >
              {(ocupado === 'traducir' || traduciendo)
                ? <><Loader2 className="mr-2 animate-spin" size={16} /> Traduciendo…</>
                : <><Languages className="mr-2" size={16} /> Regenerar traducciones</>}
            </Button>
          </Banda>
        )}

        {/* ✂️ El tope de tokens cortó una sección a media frase. Es lo que
            dejó el primer documento entregado con párrafos ilegibles donde
            iban tablas: el HTML sin cerrar se llevaba la estructura de las
            secciones siguientes. Ahora cada sección se cierra sola, pero la
            que quedó cortada sigue estando incompleta y hay que regenerarla. */}
        {cortadas.length > 0 && (
          <Banda tono="aviso">
            <Scissors size={16} />
            <div>
              <strong>
                {cortadas.length === 1
                  ? `La sección ${cortadas[0]} quedó cortada a media frase.`
                  : `Hay ${cortadas.length} secciones cortadas a media frase: ${cortadas.join(', ')}.`}
              </strong>
              <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
                El modelo se pasó del largo pedido y el texto se cortó. Regenerá
                esa sección con el botón de recargar de su fila; el resto del
                documento no está afectado.
              </div>
            </div>
          </Banda>
        )}

        {enProsa.length > 0 && (
          <Banda tono="aviso">
            <AlertTriangle size={16} />
            <div>
              <strong>
                {enProsa.length === 1
                  ? `La sección ${enProsa[0]} trae una serie de cifras escrita como párrafo.`
                  : `Hay ${enProsa.length} secciones con series de cifras escritas como párrafo: ${enProsa.join(', ')}.`}
              </strong>
              <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
                La trayectoria año por año debería ir en una tabla; en prosa no
                se puede verificar la aritmética. Regenerá esa sección: el
                prompt ahora le entrega la tabla ya armada para que la pegue.
              </div>
            </div>
          </Banda>
        )}

        {doc.financial_sheet === 'unavailable' && (
          <Banda tono="aviso">
            <AlertTriangle size={16} />
            <div>
              <strong>El plan financiero salió sin las cifras del cliente.</strong>
              <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
                La hoja compartida no se pudo derivar, así que el documento declara
                supuestos en vez de inventar un capital. Cargá el proyecto en el
                dossier del cliente y regenerá la sección 5 para que traiga las
                cifras reales.
              </div>
            </div>
          </Banda>
        )}

        {/* El interruptor de idioma: cambia lo que se LEE, igual que en la
            ficha del proyecto de la visa. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: '0.9rem 1.1rem', marginTop: '1.25rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>
              {leyendoEs ? '📄 Versión en Español' : '📄 English Version'}
            </div>
            <div style={{ color: '#6B7280', fontSize: '0.83rem', marginTop: 2 }}>
              {leyendoEs
                ? 'Traducción para revisión del cliente. El original es el inglés.'
                : 'El documento original: es el que se adjunta a la petición.'}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setIdioma(leyendoEs ? 'en' : 'es')}
            data-testid="empresarial-lang-switch"
          >
            {leyendoEs ? '🇺🇸 Switch to English' : '🇪🇸 Cambiar a Español'}
          </Button>
        </div>

        {/* VISTA PREVIA: el documento entero, como se va a entregar. Es lo
            que tiene la ficha del NIW y lo que permite leerlo sin abrir siete
            acordeones ni descargar el archivo. Se arma con las secciones que
            ya están cargadas, sin pedirle nada más al backend. */}
        {escritas > 0 && (
          <div style={{ marginTop: '1.25rem', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.9rem 1.1rem', borderBottom: verPreview ? '1px solid #F1F5F9' : 0 }}>
              <FileText size={17} style={{ color: '#0F766E' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>
                  Vista previa del documento
                </div>
                <div style={{ color: '#6B7280', fontSize: '0.82rem', marginTop: 2 }}>
                  {leyendoEs ? 'Versión en español' : 'Versión en inglés'} · {escritas} de 7 secciones
                </div>
              </div>
              <button
                onClick={() => setVerPreview(!verPreview)}
                style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.82rem', color: '#374151', cursor: 'pointer' }}
              >
                {verPreview ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            {verPreview && (
              <div style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <h2 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: '#111827', textAlign: 'center', marginTop: 0 }}>
                  {doc.project_title || 'Proyecto Empresarial'}
                </h2>
                {doc.business_name && (
                  <p style={{ textAlign: 'center', color: '#6B7280', fontSize: '0.88rem', marginTop: '-0.4rem' }}>
                    {doc.business_name}
                  </p>
                )}
                {secciones.map((s) => {
                  const cuerpo = contenidoDe(s);
                  if (!cuerpo.trim()) return null;
                  const titulo = leyendoEs
                    ? (TITULOS_ES[s.number - 1] || s.title)
                    : s.title;
                  return (
                    <div key={`prev-${s.number}`} style={{ marginTop: '1.75rem' }}>
                      <h3 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.05rem', fontWeight: 800, color: '#111827', borderBottom: '2px solid #ECFDF5', paddingBottom: '0.3rem' }}>
                        {s.number}. {titulo}
                      </h3>
                      <div
                        className="empresarial-preview"
                        style={{ fontSize: '0.92rem', lineHeight: 1.75, color: '#374151' }}
                        // Generado por nuestro propio backend a partir del
                        // proyecto del cliente, igual que en las otras fichas.
                        dangerouslySetInnerHTML={{ __html: cuerpo }}
                      />
                    </div>
                  );
                })}
                {/* Las tablas del plan financiero se desbordan en pantalla si
                    no se las deja desplazar. */}
                <style>{`
                  .empresarial-preview table { width: 100%; border-collapse: collapse; margin: 0.9rem 0; font-size: 0.85rem; display: block; overflow-x: auto; }
                  .empresarial-preview th, .empresarial-preview td { border: 1px solid #E5E7EB; padding: 0.4rem 0.55rem; text-align: left; vertical-align: top; }
                  .empresarial-preview th { background: #F8FAFC; font-weight: 700; }
                  .empresarial-preview h3, .empresarial-preview h4 { font-family: Manrope, sans-serif; color: #111827; margin-top: 1.2rem; }
                  .empresarial-preview ul, .empresarial-preview ol { padding-left: 1.4rem; }
                `}</style>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
          {secciones.map((s) => {
            const cuerpo = contenidoDe(s);
            const tiene = !!cuerpo.trim();
            const hayIngles = !!(s.content || '').trim();
            const abierto = abierta === s.number;
            return (
              <div key={s.number} style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.8rem 1rem' }}>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: '#9CA3AF', fontSize: '0.85rem', width: 16 }}>{s.number}</span>
                  <button
                    onClick={() => setAbierta(abierto ? null : s.number)}
                    style={{ flex: 1, textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', padding: 0, fontSize: '0.92rem', fontWeight: 700, color: tiene ? '#111827' : '#9CA3AF' }}
                  >
                    {s.title}
                  </button>
                  {!hayIngles && <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>sin generar</span>}
                  {hayIngles && !(s.content_es || '').trim() && (
                    <span style={{ fontSize: '0.72rem', color: '#1D4ED8', background: '#EFF6FF', padding: '0.1rem 0.4rem', borderRadius: 4 }}>
                      sin español
                    </span>
                  )}
                  {s.prose_series > 0 && (
                    <span
                      title="Una serie de cifras quedó escrita como párrafo en vez de tabla. Regenerala."
                      style={{ fontSize: '0.72rem', color: '#92400E', background: '#FFFBEB', padding: '0.1rem 0.4rem', borderRadius: 4 }}
                    >
                      cifras en prosa
                    </span>
                  )}
                  {s.truncated && (
                    <span
                      title="El texto se cortó a media frase por el tope de tokens. Regenerala."
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.72rem', color: '#92400E', background: '#FFFBEB', padding: '0.1rem 0.4rem', borderRadius: 4 }}
                    >
                      <Scissors size={11} /> cortada
                    </span>
                  )}
                  {s.status === 'edited' && <span style={{ fontSize: '0.72rem', color: '#0F766E', background: '#ECFDF5', padding: '0.1rem 0.4rem', borderRadius: 4 }}>editada</span>}
                  <button
                    onClick={() => regenerar(s.number)}
                    disabled={ocupado === `gen-${s.number}` || generando}
                    title={generando ? 'Esperá a que termine la generación' : 'Regenerar esta sección'}
                    style={{ ...botonIcono, opacity: generando ? 0.35 : 1 }}
                  >
                    {ocupado === `gen-${s.number}`
                      ? <Loader2 size={14} className="animate-spin" />
                      : <RefreshCw size={14} />}
                  </button>
                  {hayIngles && (
                    <button
                      onClick={() => {
                        // Se edita SIEMPRE el inglés: es el original, y el
                        // español se vuelve a generar a partir de él.
                        setIdioma('en');
                        setEditando(s.number);
                        setBorrador(s.content || '');
                        setAbierta(s.number);
                      }}
                      title="Editar el original en inglés"
                      style={botonIcono}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  <button onClick={() => setAbierta(abierto ? null : s.number)} style={botonIcono}>
                    {abierto ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                </div>

                {abierto && (
                  <div style={{ borderTop: '1px solid #F1F5F9', padding: '1rem' }}>
                    {editando === s.number ? (
                      <>
                        <textarea
                          value={borrador}
                          onChange={(e) => setBorrador(e.target.value)}
                          rows={16}
                          style={{ width: '100%', border: '1px solid #E5E7EB', borderRadius: 8, padding: '0.7rem', fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', lineHeight: 1.5, resize: 'vertical' }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                          <Button onClick={() => guardar(s.number)} disabled={ocupado === `save-${s.number}`}>
                            {ocupado === `save-${s.number}`
                              ? <Loader2 className="mr-2 animate-spin" size={16} />
                              : <Save className="mr-2" size={16} />}
                            Guardar
                          </Button>
                          <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
                        </div>
                        <p style={{ color: '#9CA3AF', fontSize: '0.78rem', marginTop: '0.5rem' }}>
                          Es HTML: los <code>&lt;table&gt;</code> se convierten en tablas de
                          Word y del PDF al descargar, así que conviene no aplanarlos a
                          texto. Al guardar, la versión en español de esta sección queda
                          pendiente de traducir.
                        </p>
                      </>
                    ) : tiene ? (
                      <div
                        style={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#374151' }}
                        // El contenido lo generó nuestro propio backend a partir del
                        // proyecto del cliente; es el mismo camino que usan las otras
                        // fichas de documento del panel.
                        dangerouslySetInnerHTML={{ __html: cuerpo }}
                      />
                    ) : (
                      <p style={{ color: '#9CA3AF', fontSize: '0.88rem', margin: 0 }}>
                        {faltaEspanol}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const botonIcono = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: 0, color: '#6B7280', cursor: 'pointer',
  padding: '0.25rem', borderRadius: 6,
};

const TONOS = {
  info:       { bg: '#EFF6FF', bd: '#BFDBFE', fg: '#1E3A8A' },
  traduccion: { bg: '#EFF6FF', bd: '#BFDBFE', fg: '#1E3A8A' },
  error:      { bg: '#FEF2F2', bd: '#FECACA', fg: '#7F1D1D' },
  aviso:      { bg: '#FFFBEB', bd: '#FDE68A', fg: '#78350F' },
};

function Banda({ tono, children }) {
  const c = TONOS[tono] || TONOS.info;
  return (
    <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start', background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, borderRadius: 12, padding: '0.9rem 1.1rem', marginTop: '1rem', fontSize: '0.9rem' }}>
      {children}
    </div>
  );
}
